import { z } from 'zod';

// Documentation-only schemas for the external API responses the contracts still type as z.any().
// They feed the generated Swagger doc (route metadata.responseDoc) and are never used to validate responses.

export const ExternalTransitDocSchema = z.object({
  _id: z.string().describe('unique identifier for the transit record on our system').optional(),
  name: z.string().describe('name of the transit record').optional(),
  createdAt: z.string().describe('when the transit record was created').optional(),
  updatedAt: z.string().describe('when the transit record was last updated').optional(),
  generalInfo: z.record(z.unknown()).describe('general transit information').optional(),
  TransitOperation: z.record(z.unknown()).describe('transit operation data').optional(),
  Consignment: z.record(z.unknown()).describe('consignment data').optional(),
  CustomsOfficeOfDeparture: z.record(z.unknown()).describe('customs office of departure').optional(),
  CustomsOfficeOfDestinationDeclared: z.record(z.unknown()).describe('declared customs office of destination').optional(),
  CustomsOfficeOfExitForTransitDeclared: z.array(z.record(z.unknown())).describe('declared customs offices of exit for transit').optional(),
  CustomsOfficeOfTransitDeclared: z.array(z.record(z.unknown())).describe('declared customs offices of transit').optional(),
  HolderOfTheTransitProcedure: z.record(z.unknown()).describe('holder of the transit procedure').optional(),
  TraderAtDestination: z.record(z.unknown()).describe('trader at destination').optional(),
  customsState: z.record(z.unknown()).describe('customs state information').optional(),
});

export const ExternalAuditTrailDocSchema = z.object({
  LRN: z.string().optional(),
  MRN: z.string().optional(),
  country: z.string().describe('BE or NL').optional(),
  group: z.string().describe('LV or HV').optional(),
  batch: z.string().describe('unique identifier of the batch').optional(),
  messages: z
    .array(z.record(z.unknown()))
    .describe('customs messages in arrival order, each keyed by its message code (e.g. ie428, ie429, ie451, ie460)')
    .optional(),
});

const RISK_ANALYSIS_PARAMETERS_EXAMPLE = {
  vatValidationEnabled: true,
  eoriValidationEnabled: true,
  agentPrompt: 'Flag undervalued electronics',
  evaluationMethods: {
    evalMethodAIHSCode: { activateMethod: true, range: { from: 0, to: 0.3 } },
    evalMethodWebValue: { activateMethod: true, minimumRate: 0.8 },
    evalMethodIMADBStatisticalValue: { activateMethod: true, minimumRate: 0.6 },
    evalMethodCalculator: { activateMethod: false, marketplaceFees: 0.15, fullfillmentCost: 2.5, storageCost: 0.5, otherCost: 0.02, priceDeviation: 0.2 },
    evalMethodInspection: { activateMethod: true, periodOfInspection: 90, inspectionRateOfVAT: 0.1, inspectionRateOfEORI: 0.1 },
  },
};

export const RISK_ANALYSIS_PARAMETERS_DESCRIPTION = [
  'JSON-encoded risk analysis parameters stored on the batch. All fields, including each evaluation method, are optional and may be null.',
  '',
  '- `vatValidationEnabled` (boolean): validate the intra-community VAT number of each declaration (B2B transactions only)',
  '- `eoriValidationEnabled` (boolean): validate the importer EORI number of each declaration (B2B transactions only)',
  '- `agentPrompt` (string): prompt for the AI agent; stored but not used yet',
  '- `evaluationMethods` (object): when provided, replaces the customer evaluation methods for this batch. ' +
    'Each method is enabled with `activateMethod` (boolean); all other values are numbers:',
  '  - `evalMethodAIHSCode`: accept the AI suggested HS code when its distance is within `range`. ' +
    'When `range` is sent, both `from` and `to` are required, must be between 0 and 1, and `from` must not exceed `to`',
  '  - `evalMethodWebValue`: `minimumRate` (at most 1), share of the web resale price used as the reference value',
  '  - `evalMethodIMADBStatisticalValue`: `minimumRate` (at most 1), share of the IMADB statistical value used as the reference value',
  '  - `evalMethodCalculator`: costs deducted from the web resale price: `marketplaceFees` (share of the resale price excl. VAT), ' +
    '`fullfillmentCost` and `storageCost` (EUR per unit), `otherCost` (share of the resale price), ' +
    'and `priceDeviation` (accepted deviation between the web value and the declared value, as a share)',
  '  - `evalMethodInspection`: flag VAT / EORI numbers whose in-control to released ratio over the last `periodOfInspection` days ' +
    'exceeds `inspectionRateOfVAT` / `inspectionRateOfEORI` (0 to 1)',
  '',
  '`evalMethodCalculator` and `evalMethodWebValue` cannot both be activated. Invalid parameters are rejected with a 400.',
  '',
  `Example: \`${JSON.stringify(RISK_ANALYSIS_PARAMETERS_EXAMPLE)}\``,
].join('\n');
